import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as TaxIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { todayCv } from '../../../utils/date';

interface BillingTax {
  id: string;
  code: string;
  name: string;
  description: string;
  taxType: 'percentage' | 'fixed' | 'exempt';
  rate: number;
  applicableFrom: string;
  applicableTo?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

// Mock data
const mockTaxes: BillingTax[] = [
  {
    id: '1',
    code: 'IVA_NORMAL',
    name: 'IVA Taxa Normal',
    description: 'Imposto sobre Valor Acrescentado - Taxa normal de 15%',
    taxType: 'percentage',
    rate: 15,
    applicableFrom: '2024-01-01',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    code: 'IVA_REDUZIDO',
    name: 'IVA Taxa Reduzida',
    description: 'Imposto sobre Valor Acrescentado - Taxa reduzida de 6%',
    taxType: 'percentage',
    rate: 6,
    applicableFrom: '2024-01-01',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: '3',
    code: 'IVA_ISENTO',
    name: 'IVA Isento',
    description: 'Operações isentas de IVA',
    taxType: 'exempt',
    rate: 0,
    applicableFrom: '2024-01-01',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: '4',
    code: 'TAXA_FIXA_SERV',
    name: 'Taxa Fixa Serviço',
    description: 'Taxa fixa aplicável a determinados serviços',
    taxType: 'fixed',
    rate: 500,
    applicableFrom: '2024-01-01',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: '5',
    code: 'SELO_DIGITAL',
    name: 'Imposto de Selo Digital',
    description: 'Imposto de selo sobre serviços digitais',
    taxType: 'percentage',
    rate: 2,
    applicableFrom: '2024-01-01',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: '6',
    code: 'IVA_OLD',
    name: 'IVA 10% (Descontinuado)',
    description: 'Taxa antiga de IVA - já não aplicável',
    taxType: 'percentage',
    rate: 10,
    applicableFrom: '2020-01-01',
    applicableTo: '2023-12-31',
    status: 'inactive',
    createdAt: '2020-01-01',
  },
];

const BillingTaxes: React.FC = () => {
  const [taxes, setTaxes] = useState<BillingTax[]>(mockTaxes);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedTax, setSelectedTax] = useState<BillingTax | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    description: string;
    taxType: 'percentage' | 'fixed' | 'exempt';
    rate: number;
    applicableFrom: string;
    applicableTo: string;
    status: 'active' | 'inactive';
  }>({
    code: '',
    name: '',
    description: '',
    taxType: 'percentage',
    rate: 0,
    applicableFrom: todayCv(),
    applicableTo: '',
    status: 'active',
  });

  const getTypeChip = (type: string) => {
    const config: Record<string, { color: 'primary' | 'secondary' | 'default'; label: string }> = {
      percentage: { color: 'primary', label: 'Percentual' },
      fixed: { color: 'secondary', label: 'Fixo' },
      exempt: { color: 'default', label: 'Isento' },
    };
    const { color, label } = config[type] || config.percentage;
    return <Chip size="small" color={color} label={label} />;
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />
    );
  };

  const filteredTaxes = taxes.filter(tax => {
    const matchesSearch = searchTerm === '' ||
      tax.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tax.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tax.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || tax.taxType === typeFilter;
    const matchesStatus = statusFilter === 'all' || tax.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: taxes.length,
    active: taxes.filter(t => t.status === 'active').length,
    percentage: taxes.filter(t => t.taxType === 'percentage').length,
    exempt: taxes.filter(t => t.taxType === 'exempt').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (tax?: BillingTax) => {
    if (tax) {
      setSelectedTax(tax);
      setFormData({
        code: tax.code,
        name: tax.name,
        description: tax.description,
        taxType: tax.taxType,
        rate: tax.rate,
        applicableFrom: tax.applicableFrom,
        applicableTo: tax.applicableTo || '',
        status: tax.status,
      });
    } else {
      setSelectedTax(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        taxType: 'percentage',
        rate: 0,
        applicableFrom: todayCv(),
        applicableTo: '',
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedTax(null);
  };

  const handleSave = () => {
    if (selectedTax) {
      setTaxes(taxes.map(t =>
        t.id === selectedTax.id
          ? {
              ...t,
              ...formData,
              applicableTo: formData.applicableTo || undefined,
            }
          : t
      ));
    } else {
      const newTax: BillingTax = {
        id: crypto.randomUUID(),
        ...formData,
        applicableTo: formData.applicableTo || undefined,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setTaxes([...taxes, newTax]);
    }
    handleCloseEdit();
  };

  const handleDelete = (tax: BillingTax) => {
    if (window.confirm(`Tem certeza que deseja eliminar a taxa "${tax.name}"?`)) {
      setTaxes(taxes.filter(t => t.id !== tax.id));
    }
  };

  const formatRate = (tax: BillingTax) => {
    if (tax.taxType === 'exempt') {
      return 'Isento';
    } else if (tax.taxType === 'percentage') {
      return `${tax.rate}%`;
    } else {
      return `${Math.round(tax.rate / 100).toLocaleString('pt-PT')}$`;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Taxas de Faturação
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Nova Taxa
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaxIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Taxas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaxIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.percentage}</Typography>
                  <Typography variant="caption" color="text.secondary">Percentuais</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaxIcon color="secondary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.exempt}</Typography>
                  <Typography variant="caption" color="text.secondary">Isenções</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar taxas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="percentage">Percentual</MenuItem>
                <MenuItem value="fixed">Fixo</MenuItem>
                <MenuItem value="exempt">Isento</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Taxes Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={120}>Código</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell width={120}>Tipo</TableCell>
                <TableCell width={100} align="right">Taxa</TableCell>
                <TableCell width={110}>Válido Desde</TableCell>
                <TableCell width={110}>Válido Até</TableCell>
                <TableCell width={100}>Estado</TableCell>
                <TableCell width={100} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTaxes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((tax) => (
                <TableRow key={tax.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {tax.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {tax.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                      {tax.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTypeChip(tax.taxType)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {formatRate(tax)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.75rem">
                      {tax.applicableFrom}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {tax.applicableTo ? (
                      <Typography variant="body2" fontSize="0.75rem">
                        {tax.applicableTo}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(tax.status)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(tax)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(tax)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTaxes.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TaxIcon />
          {selectedTax ? 'Editar Taxa' : 'Nova Taxa'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Código"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 20 }}
                disabled={!!selectedTax}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={formData.taxType}
                onChange={(e) => setFormData({ ...formData, taxType: e.target.value as any })}
              >
                <MenuItem value="percentage">Percentual</MenuItem>
                <MenuItem value="fixed">Fixo</MenuItem>
                <MenuItem value="exempt">Isento</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={formData.taxType === 'percentage' ? 'Taxa (%)' : formData.taxType === 'fixed' ? 'Valor Fixo (cêntimos)' : 'Valor'}
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
                disabled={formData.taxType === 'exempt'}
                helperText={
                  formData.taxType === 'percentage'
                    ? 'Percentagem (ex: 15 para 15%)'
                    : formData.taxType === 'fixed'
                    ? `${Math.round(formData.rate / 100).toLocaleString('pt-PT')}$`
                    : 'Isento de taxas'
                }
                inputProps={{
                  step: formData.taxType === 'percentage' ? 0.1 : 1,
                  min: 0,
                  max: formData.taxType === 'percentage' ? 100 : undefined
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Aplicável Desde"
                value={formData.applicableFrom}
                onChange={(e) => setFormData({ ...formData, applicableFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="date"
                label="Aplicável Até (opcional)"
                value={formData.applicableTo}
                onChange={(e) => setFormData({ ...formData, applicableTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Deixe vazio para aplicação indefinida"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.code || !formData.name || !formData.applicableFrom}
          >
            {selectedTax ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingTaxes;
